'use client'

import { useRef, useMemo, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

NProgress.configure({ showSpinner: false, trickleSpeed: 120, minimum: 0.08 })

const isInternalNavigation = (anchor: HTMLAnchorElement) => {
	if (anchor.target && anchor.target !== '_self') return false
	if (anchor.hasAttribute('download')) return false

	const href = anchor.getAttribute('href')

	if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false
	if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
		try {
			const url = new URL(href, window.location.origin)

			if (url.origin !== window.location.origin) return false
		} catch {
			return false
		}
	}

	return true
}

const RouteLoaderProvider = () => {
	// Variables
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const searchParamsString = useMemo(() => searchParams.toString(), [searchParams])
	const previousPath = useRef<string>(`${pathname}?${searchParamsString}`)
	const safetyTimeout = useRef<number | null>(null)

	// Callbacks
	useEffect(() => {
		const clearSafetyTimeout = () => {
			if (safetyTimeout.current !== null) {
				window.clearTimeout(safetyTimeout.current)
				safetyTimeout.current = null
			}
		}

		const start = () => {
			NProgress.start()
			clearSafetyTimeout()
			safetyTimeout.current = window.setTimeout(() => {
				NProgress.done()
			}, 15000)
		}

		const handleClick = (event: MouseEvent) => {
			if (event.defaultPrevented) return
			if (event.button !== 0) return
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

			const target = event.target as Element | null
			const anchor = target?.closest?.('a') as HTMLAnchorElement | null

			if (!anchor || !isInternalNavigation(anchor)) return

			const nextUrl = new URL(anchor.href, window.location.origin)
			const currentUrl = new URL(window.location.href)

			if (`${nextUrl.pathname}${nextUrl.search}` === `${currentUrl.pathname}${currentUrl.search}`) return

			start()
		}

		const handlePopState = () => {
			start()
		}

		document.addEventListener('click', handleClick)
		window.addEventListener('popstate', handlePopState)

		return () => {
			document.removeEventListener('click', handleClick)
			window.removeEventListener('popstate', handlePopState)
			clearSafetyTimeout()
		}
	}, [])

	useEffect(() => {
		const nextPath = `${pathname}?${searchParamsString}`

		if (previousPath.current === nextPath) return

		previousPath.current = nextPath
		NProgress.done()
	}, [pathname, searchParamsString])

	return null
}

export default RouteLoaderProvider
